/**
 * VireoHR - Owner Payroll Screen
 * Ultra-Minimal Design with Generate, Edit, and Export
 */

import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { firestore } from '@/lib/firebase/client';
import { exportPayrollCSV } from '@/services/cloudFunctions';
import { functions } from '@/services/cloudFunctions';


interface PayrollRecord {
  id: string;
  employeeName: string;
  totalHours: number;
  overtimeHours: number;
  grossEarnings: number;
  overtimePay: number;
  latePenalty: number;
  noShowPenalty: number;
  lateCount: number;
  noShowCount: number;
  netEarnings: number;
  adjustments: number;
  finalAmount: number;
  paid: boolean;
}

export default function Payroll() {
  const { user } = useAuth();
  const [records, setRecords] = useState<PayrollRecord[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [editingRecord, setEditingRecord] = useState<PayrollRecord | null>(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState('0');

  useEffect(() => {
    if (user?.tenantId) {
      fetchPayroll();
    }
  }, [user, selectedMonth, selectedYear]);

  const fetchPayroll = async () => {
    if (!user?.tenantId) return;

    try {
      setLoading(true);
      const payrollRef = collection(firestore, 'payroll');
      const q = query(
        payrollRef,
        where('tenantId', '==', user.tenantId),
        where('month', '==', selectedMonth),
        where('year', '==', selectedYear)
      );

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PayrollRecord[];

      setRecords(data);
    } catch (error) {
      console.error('Error fetching payroll:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePayroll = async () => {
    if (!user?.tenantId) return;

    if (!confirm(`Generate payroll for ${getMonthName(selectedMonth)} ${selectedYear}?`)) {
      return;
    }

    try {
      const loadingToast = toast.loading('Generating payroll...');
      
      const generatePayroll = httpsCallable(functions, 'generatePayroll');
      const result = await generatePayroll({
        tenantId: user.tenantId,
        month: selectedMonth,
        year: selectedYear,
      });

      toast.dismiss(loadingToast);
      
      const data = result.data as any;
      if (data.success) {
        toast.success(`Payroll generated for ${data.records} employees`);
        fetchPayroll();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate payroll');
    }
  };

  const handleExportCSV = async () => {
    if (!user?.tenantId) return;

    try {
      const loadingToast = toast.loading('Generating CSV...');
      const result = await exportPayrollCSV({
        tenantId: user.tenantId,
        month: selectedMonth,
        year: selectedYear,
      });

      toast.dismiss(loadingToast);

      if (result.data.success) {
        // Download CSV
        const blob = new Blob([result.data.csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.data.filename;
        a.click();
        toast.success('CSV exported successfully');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to export CSV');
    }
  };

  const handleSaveAdjustment = async () => {
    if (!editingRecord) return;

    try {
      const adjustment = parseFloat(adjustmentAmount);
      const newFinalAmount = editingRecord.netEarnings + adjustment;

      await updateDoc(doc(firestore, 'payroll', editingRecord.id), {
        adjustments: adjustment,
        finalAmount: newFinalAmount,
      });

      toast.success('Adjustment saved');
      setEditingRecord(null);
      setAdjustmentAmount('0');
      fetchPayroll();
    } catch (error) {
      toast.error('Failed to save adjustment');
    }
  };

  const handleMarkAsPaid = async (recordId: string) => {
    try {
      await updateDoc(doc(firestore, 'payroll', recordId), {
        paid: true,
        paidAt: new Date().toISOString(),
      });

      toast.success('Marked as paid');
      fetchPayroll();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const getMonthName = (month: number) => {
    return new Date(2024, month - 1).toLocaleDateString('en-US', { month: 'long' });
  };

  const totalAmount = records.reduce((sum, r) => sum + r.finalAmount, 0);
  const paidAmount = records.filter(r => r.paid).reduce((sum, r) => sum + r.finalAmount, 0);
  const unpaidAmount = totalAmount - paidAmount;

  if (loading) {
    return (
      <Layout title="Payroll">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Payroll">
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-base font-semibold text-gray-900">Payroll</h1>
          <div className="flex gap-2">
            <Button onClick={handleGeneratePayroll} variant="primary">
              Generate
            </Button>
            {records.length > 0 && (
              <Button onClick={handleExportCSV} variant="outline">
                Export CSV
              </Button>
            )}
          </div>
        </div>

        {/* Month Selector */}
        <Card>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Period:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {getMonthName(i + 1)}
                </option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
            >
              {[2024, 2025].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </Card>

        {/* Summary */}
        {records.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            <Card>
              <p className="text-xs text-gray-500 mb-1">Total</p>
              <p className="text-xl font-bold text-[#0D9488]">{totalAmount.toFixed(2)} JD</p>
            </Card>
            <Card>
              <p className="text-xs text-gray-500 mb-1">Paid</p>
              <p className="text-xl font-bold text-green-600">{paidAmount.toFixed(2)} JD</p>
            </Card>
            <Card>
              <p className="text-xs text-gray-500 mb-1">Unpaid</p>
              <p className="text-xl font-bold text-amber-600">{unpaidAmount.toFixed(2)} JD</p>
            </Card>
          </div>
        )}

        {/* Records */}
        {records.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No payroll records for this period</p>
              <p className="text-xs text-gray-400 mt-1">Click Generate to create payroll</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {records.map((record) => (
              <Card key={record.id}>
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{record.employeeName}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{record.totalHours.toFixed(1)} hours</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#0D9488]">{record.finalAmount.toFixed(2)} JD</p>
                      {record.paid ? (
                        <Badge variant="success" className="mt-1">Paid</Badge>
                      ) : (
                        <Button
                          onClick={() => handleMarkAsPaid(record.id)}
                          variant="primary"
                          className="mt-1 text-xs px-2 py-1"
                        >
                          Mark Paid
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Details */}
                  <div className="pt-2 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Gross:</span>
                      <span className="ml-1 text-gray-900 font-medium">{record.grossEarnings.toFixed(2)} JD</span>
                    </div>
                    {record.overtimeHours > 0 && (
                      <div>
                        <span className="text-gray-500">OT:</span>
                        <span className="ml-1 text-[#D4AF37] font-medium">+{record.overtimePay.toFixed(2)} JD</span>
                      </div>
                    )}
                    {record.latePenalty > 0 && (
                      <div>
                        <span className="text-gray-500">Late ({record.lateCount}):</span>
                        <span className="ml-1 text-red-600 font-medium">-{record.latePenalty.toFixed(2)} JD</span>
                      </div>
                    )}
                    {record.noShowPenalty > 0 && (
                      <div>
                        <span className="text-gray-500">No-show ({record.noShowCount}):</span>
                        <span className="ml-1 text-red-600 font-medium">-{record.noShowPenalty.toFixed(2)} JD</span>
                      </div>
                    )}
                    {record.adjustments !== 0 && (
                      <div>
                        <span className="text-gray-500">Adjustment:</span>
                        <span className={`ml-1 font-medium ${record.adjustments > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {record.adjustments > 0 ? '+' : ''}{record.adjustments.toFixed(2)} JD
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Edit Button */}
                  {!record.paid && (
                    <div className="pt-2 border-t border-gray-100">
                      <Button
                        onClick={() => {
                          setEditingRecord(record);
                          setAdjustmentAmount(record.adjustments.toString());
                        }}
                        variant="outline"
                        className="text-xs w-full"
                      >
                        Edit Adjustment
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Adjustment Modal */}
      {editingRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-sm w-full">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Edit Adjustment</h2>
            <p className="text-sm text-gray-600 mb-4">{editingRecord.employeeName}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Net Earnings</label>
                <p className="text-lg font-bold text-gray-900">{editingRecord.netEarnings.toFixed(2)} JD</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adjustment Amount (+ or -)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={adjustmentAmount}
                  onChange={(e) => setAdjustmentAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
                />
              </div>
              
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Final Amount</p>
                <p className="text-2xl font-bold text-[#0D9488]">
                  {(editingRecord.netEarnings + parseFloat(adjustmentAmount || '0')).toFixed(2)} JD
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSaveAdjustment} variant="primary" className="flex-1">
                Save
              </Button>
              <Button
                onClick={() => {
                  setEditingRecord(null);
                  setAdjustmentAmount('0');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Layout>
  );
}
